import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { fetchOrganizations, fetchOrgLevels } from '@/api/orgAdmin';

/** Server ceiling (`emg_shared.api.common.paginate_queryset`), matching every
 *  other one-shot "give me the whole small list" fetch in this app. */
const ORG_PAGE_SIZE = 100;

export interface SelectOption {
    value: string;
    label: string;
}

/**
 * EAA-1820 (frames 84/85/75 — every dialog that has the operator pick a target
 * đơn vị): a single page of organizations, client-side searchable via the kit
 * `Select`'s `showSearch` (same pattern as `UnitTypePicker`).
 *
 * KNOWN GAP, stated rather than silently wrong: this reads ONE page of
 * `page_size=100`. portal-api has no org search-as-you-type endpoint for this
 * picker to call, and building a debounced remote-search Select is a bigger
 * lift than this ticket's row/bulk-action scope — an installation with more than
 * 100 organizations will not see every one of them here. `UnitTypePicker` (the
 * one other org-adjacent picker in this app) carries the same ceiling for the
 * same reason.
 */
export function useOrganizationOptions(enabled: boolean): { options: SelectOption[]; loading: boolean } {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    const query = useQuery({
        queryKey: ['org-options', accessToken],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                pageSize: ORG_PAGE_SIZE,
                ordering: 'name',
            }),
        enabled: enabled && Boolean(accessToken),
        staleTime: 60_000,
    });

    const options = useMemo<SelectOption[]>(
        () => (query.data?.results ?? []).map((org) => ({ value: org.id, label: `${org.name} (${org.code})` })),
        [query.data]
    );

    return { options, loading: query.isLoading };
}

/**
 * EAA-1887 (org-sync root picker): root organizations only (`parentId: null`
 * — server-side filter, not client-side), active ones only — a selectable
 * root must itself pass `AdminAppSyncOrganizationsView`'s own is_active check,
 * so an inactive root is filtered out here rather than offered and then
 * rejected on submit. Same 100-row ceiling and staleTime as
 * `useOrganizationOptions` above, for the same reason (no server search-as-
 * you-type endpoint yet).
 */
export function useRootOrganizationOptions(enabled: boolean): { options: SelectOption[]; loading: boolean } {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    const query = useQuery({
        queryKey: ['root-org-options', accessToken],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                parentId: null,
                pageSize: ORG_PAGE_SIZE,
                ordering: 'name',
            }),
        enabled: enabled && Boolean(accessToken),
        staleTime: 60_000,
    });

    const options = useMemo<SelectOption[]>(
        () =>
            (query.data?.results ?? [])
                .filter((org) => org.is_active)
                .map((org) => ({ value: org.id, label: `${org.name} (${org.code})` })),
        [query.data]
    );

    return { options, loading: query.isLoading };
}

/** Active levels of one đơn vị, as `Select` options — `undefined`/`null` orgId
 *  disables the query rather than fetching every level in the realm. */
export function useOrgLevelOptions(
    orgId: string | null | undefined,
    enabled: boolean
): { options: SelectOption[]; loading: boolean } {
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    const query = useQuery({
        queryKey: ['org-level-options', accessToken, orgId],
        queryFn: () => fetchOrgLevels(config.apiBase, accessToken as string, orgId as string),
        enabled: enabled && Boolean(accessToken) && Boolean(orgId),
        staleTime: 30_000,
    });

    const options = useMemo<SelectOption[]>(
        () =>
            (query.data?.results ?? [])
                .filter((level) => level.is_active)
                .map((level) => ({ value: level.id, label: `${level.name} (${level.role_key})` })),
        [query.data]
    );

    return { options, loading: query.isLoading };
}
